import { useId, type FC } from 'react';
import { getClasses } from '@/_utils/classnames';
import type { ToggleProps } from './Toggle.types';
import styles from './Toggle.module.css';
import { Text, Flex } from '@/_components';

export const Toggle: FC<ToggleProps> = ({
  onChange,
  isChecked,
  isLabelHidden,
  isReverse,
  isSmall,
  isDisabled,
  helperText,
  isRequired,
  label,
  id,
}) => {
  const generatedId: string = useId();
  const toggleId: string = id || generatedId;
  const toggleClasses = getClasses(styles.toggle, {
    [styles.toggleDisabled]: isDisabled,
    [styles.toggleSmall]: isSmall,
  });

  const toggleLabelClasses = getClasses(
    styles.toggle__toggleLabel,
    'flex gap-4',
    {
      [styles.toggle__toggleLabel__reverse]: isReverse,
      'flex-y-center': !helperText,
    },
  );

  const toggleMarkClasses = getClasses(styles.toggle__toggleMark, {
    [styles.toggle__toggleMark__reverse]: isReverse,
  });

  const renderLabel: React.ReactNode = (
    <Flex isColumn={true}>
      <Text
        type={isSmall ? 'tiny' : 'small'}
        fontWeight="medium"
        color={isDisabled ? 'secondary' : 'primary'}
      >
        {label}
      </Text>
      {helperText && (
        <Text type="tiny" color="secondary">
          {helperText}
        </Text>
      )}
    </Flex>
  );

  return (
    <div className={toggleClasses}>
      <label className={toggleLabelClasses} htmlFor={toggleId}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onChange}
          required={isRequired}
          disabled={isDisabled}
          id={toggleId}
        />
        <span className={toggleMarkClasses} />
        {!isLabelHidden && renderLabel}
      </label>
    </div>
  );
};
